/**
 * LocalStorage backup and OCR verification for the sync queue.
 * Provides data safety when IndexedDB is purged.
 */

const LS_BACKUP_KEY = 'velogistique_pending_ops';

/**
 * Save a summary of pending operations to localStorage as backup.
 * Only stores IDs and types, not full data payloads.
 * @param {Array<object>} pending - Pending operations from IndexedDB
 */
export function saveBackup(pending) {
  try {
    const summary = pending.map((op) => ({
      id: op.id,
      type: op.type,
      createdAt: op.createdAt,
    }));
    localStorage.setItem(LS_BACKUP_KEY, JSON.stringify(summary));
  } catch (_err) {
    // Non-critical: localStorage backup is best-effort
  }
}

/**
 * Get the localStorage backup of pending operations.
 * Useful if IndexedDB is purged to alert the user.
 * @returns {Array<{id: number, type: string, createdAt: string}>}
 */
export function getLocalStorageBackup() {
  try {
    const raw = localStorage.getItem(LS_BACKUP_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_err) {
    return [];
  }
}

/**
 * Check if there are lost operations (in localStorage but not in IndexedDB).
 * @param {Array<object>} currentPending - Current pending ops from IndexedDB
 * @returns {{lost: boolean, lostOps: Array<{id: number, type: string, createdAt: string}>}}
 */
export function detectLostOperations(currentPending) {
  const backup = getLocalStorageBackup();
  if (backup.length === 0) return { lost: false, lostOps: [] };

  const pendingIds = new Set(currentPending.map((op) => op.id));
  const lostOps = backup.filter((op) => !pendingIds.has(op.id));
  return { lost: lostOps.length > 0, lostOps };
}

/**
 * Process an OCR verification operation: send photo to OCR API,
 * compare result with manual entry, create notification on mismatch.
 * @param {object} op - The queued operation
 * @param {object} headers - HTTP headers with auth
 * @returns {Promise<void>}
 */
export async function processOcrVerify(op, headers) {
  const { image, mediaType, manualSerial, itemId, campaignId } = op.body;

  const ocrRes = await fetch(`/api/c/${campaignId}/scan`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ image, mediaType: mediaType || 'image/jpeg' }),
  });

  if (!ocrRes.ok) return;

  const ocrData = await ocrRes.json();
  const ocrSerial = (ocrData.serialNumber || '').trim().toLowerCase();
  const manual = (manualSerial || '').trim().toLowerCase();

  if (ocrSerial && manual && ocrSerial !== manual) {
    await fetch(`/api/c/${campaignId}/notifications`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        type: 'ocr_mismatch',
        message: `Ecart OCR detecte: saisie manuelle "${manualSerial}" vs OCR "${ocrData.serialNumber}" (item ${itemId})`,
        data: { itemId, manualSerial, ocrSerial: ocrData.serialNumber, confidence: ocrData.confidence },
      }),
    }).catch(() => {});
  }
}
